// C:\Users\User\Desktop\Bresco\bresco-franquicies-frontend\src\components\ui\Table\Table.js
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef } from 'react'
import { LuMoveDown, LuMoveUp } from 'react-icons/lu'
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table'
import TableActionBar from './TableActionBar'
import TableFooter from './TableFooter'

export default function Table({
  data,
  rawData,
  columns,
  tableName,
  createNewItemURL,
  allowImportExcel,
  uploadExcelURL,
  handleRefresh,
  handleAnalyzeExcel,
  onPageRowsChange,
  exportMRWUrl,
  exportExtraParams,
  initialFilters
}) {
  const topScrollRef = useRef(null)
  const bottomScrollRef = useRef(null)
  const isSyncingRef = useRef(false)

  const handleTopScroll = () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    if (bottomScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft
    }
    isSyncingRef.current = false
  }

  const handleBottomScroll = () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft
    }
    isSyncingRef.current = false
  }

  function dateBetweenFilterFn(rows, id, filterValues) {
    const sd = filterValues[0] ? new Date(filterValues[0]) : null
    const ed = filterValues[1] ? new Date(filterValues[1]) : null
    const normalizeDate = (date) => {
      if (!date) return null
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    }
    const nsd = normalizeDate(sd)
    const ned = normalizeDate(ed)
    return rows.filter((r) => {
      const time = normalizeDate(new Date(r.values[id]))
      let result = true
      if (filterValues.length === 0 || (!nsd && !ned)) result = true
      else if (nsd && !ned) result = time >= nsd
      else if (!nsd && ned) result = time <= ned
      else result = time >= nsd && time <= ned
      return result
    })
  }
  dateBetweenFilterFn.autoRemove = (val) => !val

  const filterTypes = useMemo(() => ({ dateBetween: dateBetweenFilterFn }), [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    setAllFilters,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, filters: filterState }
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: { pageSize: 25, filters: initialFilters || [] }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    if (typeof onPageRowsChange === 'function') {
      onPageRowsChange(page)
    }
  }, [page, onPageRowsChange])

  // Sincroniza el ancho del div fantasma con el scrollWidth real de la tabla
  useEffect(() => {
    const bottom = bottomScrollRef.current
    const top = topScrollRef.current
    if (!bottom || !top) return

    const updateWidth = () => {
      // scrollWidth del contenedor inferior = ancho real del contenido
      top.firstChild.style.width = bottom.scrollWidth + 'px'
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [page, columns, data])

  return (
    <>
      <div className="w-full mx-auto">
        <TableActionBar
          tableName={tableName}
          createNewItemURL={createNewItemURL}
          datos={data}
          rawData={rawData}
          rows={rows}
          page={page}
          columns={columns}
          setAllFilters={setAllFilters}
          allowImportExcel={allowImportExcel}
          uploadExcelURL={uploadExcelURL}
          handleRefresh={handleRefresh}
          handleAnalyzeExcel={handleAnalyzeExcel}
          exportMRWUrl={exportMRWUrl}
          filterState={filterState}
          exportExtraParams={exportExtraParams}
        />
        <div className="flex flex-col mt-4">

          {/* Barra de scroll superior sincronizada */}
          <div
            ref={topScrollRef}
            onScroll={handleTopScroll}
            style={{ overflowX: 'auto', overflowY: 'hidden', height: '12px' }}
          >
            {/* Div fantasma: su ancho se iguala al scrollWidth de la tabla */}
            <div style={{ height: '1px' }} />
          </div>

          {/* Scroll horizontal con la tabla */}
          <div
            ref={bottomScrollRef}
            onScroll={handleBottomScroll}
            className="w-full overflow-x-auto"
          >
            <div className="inline-block min-w-full py-2 align-middle">
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 md:rounded-lg">
                <table
                  {...getTableProps()}
                  className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    {headerGroups.map((headerGroup) => (
                      <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((header) => {
                          const { key, ...headerProps } = header.getHeaderProps(
                            header.getSortByToggleProps({
                              title: header.TitleDescription
                            })
                          )
                          const colWidth = header.minWidth || header.width || 120
                          return (
                            <th
                              key={key}
                              scope="col"
                              style={{ width: colWidth, minWidth: colWidth }}
                              className="py-3.5 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
                              {...headerProps}
                            >
                              <div className="flex flex-col justify-between h-full space-y-2">
                                <div
                                  className={`${
                                    header?.Filter
                                      ? 'flex-row justify-start items-center text-left'
                                      : 'justify-center items-center h-full'
                                  } flex w-full text-sm select-none`}
                                >
                                  <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                                    {header.render('Header')}
                                  </span>
                                  <div>
                                    {header.isSorted
                                      ? header.isSortedDesc
                                        ? <LuMoveDown size={15} />
                                        : <LuMoveUp size={15} />
                                      : ''}
                                  </div>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                  {header.Filter ? header.render('Filter') : null}
                                </div>
                              </div>
                            </th>
                          )
                        })}
                      </tr>
                    ))}
                  </thead>
                  {rows?.length > 0 ? (
                    <tbody
                      className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      {page.map((row) => {
                        prepareRow(row)
                        const { key, ...rowProps } = row.getRowProps()
                        return (
                          <tr key={key} {...rowProps}>
                            {row.cells.map((cell) => {
                              const cellProps = cell.getCellProps()
                              const { key: cellKey, ...cellRest } = cellProps
                              const colWidth = cell.column.minWidth || cell.column.width || 120
                              return (
                                <td
                                  key={cellKey}
                                  {...cellRest}
                                  style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                                  className="px-4 py-4 text-sm font-medium text-gray-700 overflow-hidden"
                                >
                                  <div className="overflow-hidden whitespace-nowrap text-ellipsis w-full">
                                    {cell.render('Cell')}
                                  </div>
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  ) : (
                    <tbody
                      className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      <tr>
                        <td
                          className="px-4 py-4 text-sm font-medium text-gray-700 whitespace-nowrap"
                          colSpan={columns.length}
                        >
                          No s&apos;han trobat registres
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 sm:flex sm:items-center sm:justify-between">
          <TableFooter
            previousPage={previousPage}
            canPreviousPage={canPreviousPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            nextPage={nextPage}
            canNextPage={canNextPage}
            gotoPage={gotoPage}
            rows={rows}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  )
}

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  rawData: PropTypes.array,
  tableName: PropTypes.string.isRequired,
  createNewItemURL: PropTypes.string.isRequired,
  allowImportExcel: PropTypes.bool,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func,
  onPageRowsChange: PropTypes.func,
  exportMRWUrl: PropTypes.string,
  exportExtraParams: PropTypes.object,
  initialFilters: PropTypes.array
}